import React, { useState } from 'react';
import styled from 'styled-components';
import { 
  FaPlus,
  FaEdit,
  FaTrash,
  FaSave,
  FaUserTag,
  FaUsers
} from 'react-icons/fa';

// Import your exact components (these remain exactly the same)
import './Designation.css';
import img1 from './img/img1.png';
import img2 from './img/img2.png';
import AddDesignation from './AddDesignation.js';

// Styled Components for modern design
const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 2rem;
  background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
  min-height: 100vh;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 2rem;
  
  @media (max-width: 768px) {
    flex-direction: column;
    gap: 1rem;
    align-items: stretch;
  }
`;

const Title = styled.h1`
  font-size: 2.5rem;
  font-weight: 700;
  color: #212529;
  margin: 0;
  display: flex;
  align-items: center;
  gap: 1rem;
  
  svg {
    font-size: 2rem;
    color: #0d6efd;
  }
  
  @media (max-width: 768px) {
    font-size: 2rem;
    justify-content: center;
  }
`;

const AddButton = styled.button`
  background: linear-gradient(135deg, #0d6efd 0%, #4285f4 100%);
  color: white;
  padding: 0.75rem 1.5rem;
  border: none;
  border-radius: 12px;
  cursor: pointer;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 0.75rem;
  transition: all 0.3s ease;
  font-size: 1rem;
  
  svg {
    font-size: 1.1rem;
  }
  
  &:hover {
    background: linear-gradient(135deg, #0a58ca 0%, #1a73e8 100%);
    transform: translateY(-2px);
    box-shadow: 0 8px 25px rgba(13, 110, 253, 0.3);
  }
`;

const TableCard = styled.div`
  background: white;
  border-radius: 16px;
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.1);
  overflow: hidden;
`;

const TableHeader = styled.div`
  background: linear-gradient(135deg, #0d6efd 0%, #4285f4 100%);
  color: white;
  padding: 1.5rem 2rem;
`;

const TableTitle = styled.h2`
  margin: 0;
  font-size: 1.5rem;
  font-weight: 700;
  display: flex;
  align-items: center;
  gap: 0.75rem;
  
  svg {
    font-size: 1.3rem;
  }
`;

const TableContainer = styled.div`
  padding: 0;
  background: white;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 0.95rem;
`;

const TableHead = styled.thead`
  background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
`;

const TableRow = styled.tr`
  transition: all 0.2s ease;
  
  &:hover {
    background: #f8f9fa;
  }
  
  &:not(:last-child) {
    border-bottom: 1px solid #dee2e6;
  }
`;

const TableHeaderCell = styled.th`
  padding: 1rem 1.5rem;
  text-align: left;
  font-weight: 600;
  color: #495057;
  font-size: 0.875rem;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  border-bottom: 2px solid #dee2e6;
  
  &:first-child {
    width: 8%;
    text-align: center;
  }
  
  &:nth-child(2) {
    width: 75%;
  }
  
  &:last-child {
    width: 17%;
    text-align: center;
  }
`;

const TableCell = styled.td`
  padding: 1rem 1.5rem;
  vertical-align: middle;
  
  &:first-child {
    text-align: center;
    font-weight: 600;
    color: #0d6efd;
  }
  
  &:last-child {
    text-align: center;
  }
`;

const EditInput = styled.input`
  width: 100%;
  padding: 0.5rem 0.75rem;
  border: 2px solid #0d6efd;
  border-radius: 8px;
  font-size: 0.95rem;
  transition: all 0.3s ease;
  
  &:focus {
    outline: none;
    border-color: #0a58ca;
    box-shadow: 0 0 0 0.2rem rgba(13, 110, 253, 0.15);
  }
`;

const ActionButton = styled.button`
  background: ${props => props.save ? 
    'linear-gradient(135deg, #28a745 0%, #20c997 100%)' : 
    'transparent'};
  color: ${props => props.save ? 'white' : '#6c757d'};
  border: ${props => props.save ? 'none' : '1px solid transparent'};
  padding: ${props => props.save ? '0.5rem 1rem' : '0.5rem'};
  border-radius: ${props => props.save ? '8px' : '50%'};
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-weight: 600;
  margin: 0 0.25rem;
  font-size: 0.9rem;
  
  svg {
    font-size: ${props => props.save ? '1rem' : '1.1rem'};
  }
  
  &:hover {
    background: ${props => props.save ? 
      'linear-gradient(135deg, #218838 0%, #1ea97c 100%)' : 
      '#f8f9fa'};
    color: ${props => props.save ? 'white' : '#495057'};
    transform: ${props => props.save ? 'translateY(-1px)' : 'scale(1.1)'};
  }
`;

const ActionGroup = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
`;

const IconWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  cursor: pointer;
  border-radius: 50%;
  transition: all 0.3s ease;
  
  &:hover {
    background: #f8f9fa;
    transform: scale(1.1);
  }
  
  &.edit:hover {
    background: rgba(13, 110, 253, 0.1);
  }
  
  &.delete:hover {
    background: rgba(220, 53, 69, 0.1);
  }
  
  img {
    width: 40px;
    height: 40px;
  }
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 3rem 2rem;
  color: #6c757d;
`;

const EmptyIcon = styled.div`
  font-size: 5rem;
  color: #dee2e6;
  margin-bottom: 1rem;
  
  svg {
    font-size: 5rem;
  }
`;

// EXACT ORIGINAL COMPONENT WITH PRESERVED LOGIC - NO FUNCTIONAL CHANGES
const DesignationTable = () => {
  // EXACT ORIGINAL STATE - NO CHANGES
  const [designations, setDesignations] = useState([
    { id: 1, name: 'Admin' },
    { id: 2, name: 'Super Admin' },
    { id: 3, name: 'Managing Director' },
    { id: 4, name: 'HR Department Head' },
    { id: 5, name: 'Manager - Finance' },
  ]);

  const [isEditing, setIsEditing] = useState(null);
  const [newDesignationName, setNewDesignationName] = useState('');
  const [showAddForm, setShowAddForm] = useState(false);

  // EXACT ORIGINAL FUNCTIONS - NO CHANGES
  const handleEditDesignation = (designation) => {
    setIsEditing(designation.id);
    setNewDesignationName(designation.name);
  };

  const handleSaveEdit = (designationId) => {
    setDesignations(designations.map(designation =>
      designation.id === designationId ? { ...designation, name: newDesignationName } : designation
    ));
    setIsEditing(null);
    setNewDesignationName('');
  };

  const handleDeleteDesignation = (designationId) => {
    setDesignations(designations.filter(designation => designation.id !== designationId));
  };

  const handleAddDesignation = (designationName) => {
    const newDesignation = {
      id: designations.length + 1,
      name: designationName,
    };
    setDesignations([...designations, newDesignation]);
  };

  const handleAddButtonClick = () => {
    setShowAddForm(true);
  };

  const handleCloseAddForm = () => {
    setShowAddForm(false);
  };

  return (
    <Container>
      <Header>
        <Title>
          <FaUserTag />
          Designations
        </Title>
        <AddButton onClick={handleAddButtonClick}>
          <FaPlus />
          Add Designation
        </AddButton>
      </Header>

      {/* Preserving exact AddDesignation logic */}
      {showAddForm && (
        <AddDesignation
          onAddDesignation={handleAddDesignation}
          onClose={handleCloseAddForm}
        />
      )}

      <TableCard>
        <TableHeader>
          <TableTitle>
            <FaUsers />
            Designation Management
          </TableTitle>
        </TableHeader>

        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableHeaderCell>#</TableHeaderCell>
                <TableHeaderCell>Designation Name</TableHeaderCell>
                <TableHeaderCell>Actions</TableHeaderCell>
              </TableRow>
            </TableHead>
            <tbody>
              {designations.length > 0 ? (
                designations.map((designation) => (
                  <TableRow key={designation.id}>
                    <TableCell>{designation.id}</TableCell>
                    <TableCell>
                      {isEditing === designation.id ? (
                        <EditInput
                          type="text"
                          value={newDesignationName}
                          onChange={(e) => setNewDesignationName(e.target.value)}
                          autoFocus
                        />
                      ) : (
                        designation.name
                      )}
                    </TableCell>
                    <TableCell>
                      {isEditing === designation.id ? (
                        <ActionGroup>
                          <ActionButton 
                            save 
                            onClick={() => handleSaveEdit(designation.id)}
                          >
                            <FaSave />
                            Save
                          </ActionButton>
                        </ActionGroup>
                      ) : (
                        <ActionGroup>
                          <IconWrapper 
                            className="edit"
                            onClick={() => handleEditDesignation(designation)}
                          >
                            <img src={img1} alt="edit" />
                          </IconWrapper>
                          <IconWrapper 
                            className="delete"
                            onClick={() => handleDeleteDesignation(designation.id)}
                          >
                            <img src={img2} alt="delete" />
                          </IconWrapper>
                        </ActionGroup>
                      )}
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan="3">
                    <EmptyState>
                      <EmptyIcon>
                        <FaUserTag />
                      </EmptyIcon>
                      <h3>No Designations Found</h3>
                      <p>Click "Add Designation" to create your first designation.</p>
                    </EmptyState>
                  </TableCell>
                </TableRow>
              )}
            </tbody>
          </Table>
        </TableContainer>
      </TableCard>
    </Container>
  );
};

export default DesignationTable;